import {
  Component,
  computed,
  inject,
  Input,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { MatIcon } from '@angular/material/icon';

export interface Position {
  x: number;
  y: number;
}

/**
 *  Avatars are used to show a visual representation of an individual
 */
@Component({
  selector: 'mulo-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  imports: [MatIcon, SvgViewboxDirective],
  host: {
    '[class.mulo-avatar]': 'true',
  },
})
export class AvatarComponent {
  private sanitizer = inject(DomSanitizer);

  /**
   * @internal
   */
  _image;

  /**
   *  The url for the image, can be base64 too
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get image() {
    return this._image;
  }

  set image(val: string) {
    if (val == null || val === '') {
      val = null;
      this.imageLoaded.set(false);
    }
    this._image = this.sanitizer.bypassSecurityTrustResourceUrl(val);
  }

  readonly imgSet = input<string[]>([]);

  /**
   * square or round image
   */
  readonly shape = input<'square' | 'round'>('square');
  /**
   * The description to go into the `alt` attribute
   */
  readonly description = input<string>(undefined);
  /**
   * Image size (1:1 ratio)
   */
  readonly size = input<string | number>(undefined);
  /**
   * Rounded border radius
   */
  readonly radius = input<number>(4);
  /**
   * Image position override; helps when the image shouldn’t be centered
   */
  readonly position = input<Position>();
  /**
   * Initials to show in the absence of image
   */
  readonly initials = input<string>();
  /**
   * Image overlay (optional) state
   * @internal
   */
  readonly overlayVisible = signal(false);
  /**
   * Flag when avatar image loaded
   * @internal
   */
  readonly imageLoaded = signal(false);

  readonly sizeString = computed(() => {
    // check if size input is a number, or a string (with or without a unit)
    const size = this.size();
    if (typeof size === 'number' || /^\d+$/.test(size)) {
      return `${size}px`;
    } else {
      return size;
    }
  });

  /**
   * @internal
   */
  readonly calcFontSize = computed(() =>
    this.sanitizer.bypassSecurityTrustStyle(
      `calc((${this.size()} * 0.025) * 1rem)`,
    ),
  );

  /**
   * @internal
   */
  showOverlay() {
    this.overlayVisible.set(true);
  }

  /**
   * @internal
   */
  hideOverlay() {
    this.overlayVisible.set(false);
  }
}
